import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, get, patch, post, type NotificationPage } from '../../services/api';
import { useAuth } from '../../hooks/AuthContext';
import { Empty, ErrorBox, Loading, PageHeader } from '../../components/Ui';
import { Icon } from '../../components/Icon';
import { fullDate, timeAgo } from '../../shared/utils/format';
import { useAsync, useTitle } from '../../hooks';
import { useNotifications } from '../../hooks/NotificationsContext';
import type { NotificationView } from '../../shared/types/api';
import { KIND_LABEL, threadPath } from '../../shared/utils/conversations';

const PER_PAGE = 20;

const THREAD_VERB: Record<string, { heading: string; verb: string }> = {
  thread_opened: { heading: 'New', verb: 'opened' },
  thread_comment: { heading: 'New comment on', verb: 'commented on' },
  thread_review: { heading: 'New review of', verb: 'reviewed' },
  thread_closed: { heading: 'Closed', verb: 'closed' },
  thread_reopened: { heading: 'Reopened', verb: 'reopened' },
  thread_merged: { heading: 'Merged', verb: 'merged' },
  thread_updated: { heading: 'Updated', verb: 'updated' },
};

/** What a notification says, where it leads and which icon fits. Unknown events still get a safe link. */
function describe(item: NotificationView) {
  const thread = item.thread;
  if (thread) {
    const words = THREAD_VERB[item.eventType] ?? { heading: 'Activity on', verb: 'changed' };
    const kind = KIND_LABEL[thread.kind].one;
    return {
      icon: (item.eventType === 'thread_merged' ? 'merged' : thread.kind === 'discussion' ? 'chat' : thread.kind) as
        | 'merged'
        | 'chat'
        | 'issue'
        | 'proposal',
      heading: `${words.heading} ${kind}`,
      action: `${words.verb} ${kind} #${thread.number}${thread.title ? ` “${thread.title}”` : ''} in`,
      target: threadPath(`/${item.repository.fullName}`, thread.kind, thread.number),
    };
  }
  const revision = item.eventType === 'revision';
  return {
    icon: (revision ? 'history' : 'shield') as 'history' | 'shield',
    heading: revision ? 'New revision' : 'Wiki access updated',
    action: item.actor ? (revision ? 'updated' : 'changed your access to') : 'An update was made to',
    target: item.revisionId
      ? `/${item.repository.fullName}/commit/${encodeURIComponent(item.revisionId)}`
      : `/${item.repository.fullName}`,
  };
}

export function Notifications() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const setPage = (value: number) => setParams({ page: String(value) });
  const { reload: refreshCount } = useNotifications();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [actionError, setActionError] = useState<ApiError>();
  const result = useAsync(
    () => get<NotificationPage>(`/user/notifications?page=${page}&perPage=${PER_PAGE}`),
    [user?.id, page],
  );
  useTitle('Notifications');

  const markRead = async (id: string, read: boolean) => {
    setActionError(undefined);
    setBusy(true);
    try {
      await patch(`/user/notifications/${encodeURIComponent(id)}`, { read });
      result.reload();
      refreshCount();
      setMessage(read ? 'Notification marked read.' : 'Notification marked unread.');
    } catch (cause) {
      setActionError(
        cause instanceof ApiError ? cause : new ApiError(0, 'error', 'Could not update the notification.'),
      );
    } finally {
      setBusy(false);
    }
  };
  const markAllRead = async () => {
    setActionError(undefined);
    setBusy(true);
    try {
      await post('/user/notifications/read-all');
      result.reload();
      refreshCount();
      setMessage('All notifications marked read.');
    } catch (cause) {
      setActionError(
        cause instanceof ApiError ? cause : new ApiError(0, 'error', 'Could not update notifications.'),
      );
    } finally {
      setBusy(false);
    }
  };

  if (result.error?.status === 404) {
    return (
      <div className="container medium">
        <ErrorBox error={result.error} retry={result.reload} />
      </div>
    );
  }

  const totalPages = result.data ? Math.max(1, Math.ceil(result.data.total / result.data.perPage)) : 1;
  return (
    <div className="container medium stack">
      <PageHeader
        title="Notifications"
        description="Updates from wikis you can currently read."
        actions={
          result.data?.unreadCount ? (
            <button className="btn" disabled={busy} onClick={() => void markAllRead()}>
              <Icon name="check" />
              Mark all read
            </button>
          ) : undefined
        }
      />
      {actionError && <ErrorBox error={actionError} />}
      {message && (
        <div className="sr-only" role="status">
          {message}
        </div>
      )}
      {result.loading && !result.data ? (
        <Loading />
      ) : result.error ? (
        <ErrorBox error={result.error} retry={result.reload} />
      ) : result.data?.items.length === 0 ? (
        <Empty title="You're all caught up">Wiki updates you subscribe to will appear here.</Empty>
      ) : (
        <>
          <section className="card notification-list" aria-label="Your notifications">
            {result.data?.items.map((item) => {
              const { icon, heading, action, target } = describe(item);
              return (
                <article className={`notification-row${item.readAt ? '' : ' unread'}`} key={item.id}>
                  <div className="notification-dot" aria-hidden="true" />
                  <Icon name={icon} />
                  <div className="notification-copy">
                    <strong>{heading}</strong>
                    <span className="badge">{item.readAt ? 'Read' : 'Unread'}</span>
                    <span>
                      {item.actor ? `${item.actor} ` : ''}
                      {action} <Link to={target}>{item.repository.fullName}</Link>
                    </span>
                    <time dateTime={item.createdAt} title={fullDate(item.createdAt)}>
                      {timeAgo(item.createdAt)}
                    </time>
                  </div>
                  <button
                    className="btn sm"
                    disabled={busy}
                    aria-label={`${item.readAt ? 'Mark unread' : 'Mark read'}: ${item.repository.fullName}`}
                    onClick={() => void markRead(item.id, !item.readAt)}
                  >
                    {item.readAt ? 'Mark unread' : 'Mark read'}
                  </button>
                </article>
              );
            })}
          </section>
          <div className="pagination">
            <span>
              {result.data?.total ?? 0} notifications · page {page} of {totalPages}
            </span>
            <div className="row">
              <button className="btn sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <button className="btn sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
